import {RsnSkeleton} from '@raison/ui-kit';
import classNames from 'classnames';
import React, {Fragment} from 'react';
import {LoadProblemPane} from '~/entities/fallbacks';
import {TransactionSchema} from '~/entities/transactions-history';
import TransactionItem from './TransactionItem/TransactionItem';
import scss from './TransactionsList.module.scss';

export function TransactionsList({
    hasFilters,
    orders,
    onOrderClick,
    isFetching,
    isLoading,
    isError,
    totalLength,
    currentLength,
    refetch,
    containerRef: ref,
    children: {nothingFound: renderNothingFound, zeroActivity: renderZeroActivity},
}: {
    orders: Map<string, TransactionSchema[]> | TransactionSchema[];
    onOrderClick?: (order: TransactionSchema) => void;
    hasFilters: boolean;

    children: {
        zeroActivity: () => React.JSX.Element;
        nothingFound: () => React.JSX.Element;
    };

    isFetching: boolean;
    isLoading: boolean;
    isError: boolean;
    totalLength: number | undefined;
    currentLength: number;
    containerRef?: React.RefCallback<Element>;
    refetch: () => void;
}) {
    if ('size' in orders && orders.size > 0) {
        return container(renderGroupedList(orders));
    }
    if ('length' in orders && orders.length > 0) {
        return container(renderFlatList(orders));
    }

    if (isLoading || isError) {
        return container(renderSkeleton());
    }

    if (hasFilters) {
        return container(renderNothingFound());
    } else {
        return container(renderZeroActivity());
    }

    function container(content: React.JSX.Element) {
        return <div className={scss.listContainer}>{content}</div>;
    }

    function renderFlatList(orders: TransactionSchema[]) {
        return (
            <Fragment>
                {orders.map((order, j) => (
                    <TransactionItem
                        onClick={onOrderClick && (() => onOrderClick(order))}
                        containerRef={j === orders.length - 1 ? ref : undefined}
                        key={order.id}
                        order={order}
                    />
                ))}
                {renderSkeleton()}
            </Fragment>
        );
    }

    function renderGroupedList(groupedOrders: Map<string, TransactionSchema[]>) {
        return (
            <Fragment>
                {Array.from(groupedOrders, ([dateString, orders], i) => (
                    <Fragment key={dateString}>
                        <div className={scss.dateGroupHeader}>{dateString}</div>
                        {orders.map((order, j) => (
                            <TransactionItem
                                onClick={onOrderClick && (() => onOrderClick(order))}
                                containerRef={i === groupedOrders.size - 1 && j === orders.length - 1 ? ref : undefined}
                                key={order.id}
                                order={order}
                            />
                        ))}
                    </Fragment>
                ))}
                {renderSkeleton()}
            </Fragment>
        );
    }

    function renderSkeleton() {
        return (
            <div className={classNames(scss.centeringContainer, scss.heightItemLike, scss.pageBottomMargin)}>
                {(isFetching || isError) && (
                    <TransactionsListSkeleton totalLength={totalLength} currentLength={currentLength} />
                )}
                {isError && <LoadProblemPane reset={refetch} />}
            </div>
        );
    }
}

function TransactionsListSkeleton({
    totalLength,
    currentLength,
}: {
    totalLength: number | undefined;
    currentLength: number;
}) {
    const computedLength = (totalLength ?? Infinity) - currentLength;
    const length = Math.min(10, computedLength);
    return (
        <Fragment>
            {!totalLength && <RsnSkeleton containerClassName='mb-3 justify-self-start' width={96} height={16} />}
            {Array.from({length: length}, (_, i) => i).map(idx => (
                <TransactionItem.Skeleton key={idx} index={idx} />
            ))}
        </Fragment>
    );
}
